import {ActionCreator, createAction} from '@packages/utilities/create-action';
import {Component} from '@packages/systems/wfdl';
import {type NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {reportBug} from '@packages/utilities/reporter';
import {emptyObject} from '@packages/utilities/fp/utils';
import {transformNodeClickedPayload} from './transformNodeClickedPayload';
import type {NodeClickedPayload} from './types';
import {NODE_CLICKED} from '@packages/systems/designer/actions';

export const getNodeClickedActionCreator = (
  components: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[]
) => {
  return _nodeClickedActionCreatorFactory(
    components,
    focusedSymbolNativeIdPath
  );
};

type AnyStateType = any;

const DUMMY_ACTION: any = emptyObject;

export const getNodeClickedActionCreatorFromState = (
  state: AnyStateType
): ActionCreator<'NODE_CLICKED', NodeClickedPayload> => {
  const components = state?.DesignerStore?.components;
  const focusedSymbolNativeIdPath =
    state?.UiNodeStore?.focusedSymbolNativeIdPath;

  if (!components || !focusedSymbolNativeIdPath) {
    reportBug(
      'DesignerStateMissingArgumentInNodeClickedActionCreator',
      'Attempted to create a NODE_CLICKED action creator from state in `getNodeClickedActionCreatorFromState`, but provided state had missing dependencies!',
      {
        team: 'dxp',
        ticket: 'https://webflow.atlassian.net/browse/DESXP-6522',
        data: {
          components,
          focusedSymbolNativeIdPath,
        },
      }
    );

    // If we reported the above bug, return an action creator that returns
    // an empty object as an action so that it is ignored by any reducer that may
    // be expecting NODE_CLICKED.
    return () => DUMMY_ACTION;
  }

  return _nodeClickedActionCreatorFactory(
    components,
    focusedSymbolNativeIdPath
  );
};

const _nodeClickedActionCreatorFactory = (
  components: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[]
): ActionCreator<'NODE_CLICKED', NodeClickedPayload> => {
  return (payload: NodeClickedPayload) => {
    const transformedPayload = transformNodeClickedPayload(
      payload,
      components,
      focusedSymbolNativeIdPath
    );
    return createAction<typeof NODE_CLICKED, NodeClickedPayload>(
      'NODE_CLICKED'
    )(transformedPayload);
  };
};
